import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAddSystemFormData, createSystemAsset } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';
import '../../../../styles/colors.css';

function AddSystemModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    asset_name: '',
    asset_description: '',
    asset_parent: '0',
    asset_support: '',
    asset_sla: '',
    asset_system: 1
  });
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchFormData();
    }
  }, [isOpen]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      const data = await getAddSystemFormData();
      if (data && data.systems) {
        setSystems(data.systems);
      }
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.asset_name.trim()) {
      toast.error('System name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Add the required hidden fields that the PHP backend expects for systems
      // This matches exactly what the original PHP form sends
      const systemData = {
        ...formData,
        asset_license_key: '',
        asset_category_id: '0',
        asset_vendor_id: '0', 
        asset_cost: '0',
        asset_users: '0',
        asset_date_purchased: '',
        asset_expiry: '',
        asset_system: '1' // This is the key flag that identifies it as a system
      };
      
      const result = await createSystemAsset(systemData);
      
      if (result) {
        toast.success('System created successfully!');
        onSuccess();
        setFormData({
          asset_name: '',
          asset_description: '',
          asset_parent: '0',
          asset_support: '',
          asset_sla: '',
          asset_system: 1
        });
      }
    } catch (error) {
      console.error('Error creating system:', error);
      toast.error('Failed to create system');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      asset_name: '',
      asset_description: '',
      asset_parent: '0',
      asset_support: '',
      asset_sla: '',
      asset_system: 1
    });
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Add New System">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'var(--color-primary)' }}></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New System">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-md p-4" style={{ borderColor: 'var(--border-light)' }}>
          <h4 className="text-sm font-semibold mb-3 flex items-center" style={{ color: 'var(--text-primary)' }}>
            <svg className="w-4 h-4 mr-2" style={{ color: 'var(--color-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Basic Information
          </h4>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                System Name *
              </label>
              <input
                type="text"
                name="asset_name"
                value={formData.asset_name}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  '--tw-ring-color': 'var(--color-primary)'
                }}
                placeholder="Enter system name"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Description
              </label>
              <textarea
                name="asset_description"
                value={formData.asset_description}
                onChange={handleInputChange}
                rows={2}
                className="w-full border rounded-md px-3 py-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  '--tw-ring-color': 'var(--color-primary)'
                }}
                placeholder="System description"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Parent System
              </label>
              <select
                name="asset_parent"
                value={formData.asset_parent}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  '--tw-ring-color': 'var(--color-primary)'
                }}
              >
                <option value="0">None (Root System)</option>
                {systems.map((system) => (
                  <option key={system.asset_id} value={system.asset_id}>
                    {system.asset_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                SLA Response Time
              </label>
              <input
                type="text"
                name="asset_sla"
                value={formData.asset_sla}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2"
                style={{
                  borderColor: 'var(--border-light)',
                  backgroundColor: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  '--tw-ring-color': 'var(--color-primary)'
                }}
                placeholder="e.g., 24 hours"
              />
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div className="bg-green-50 rounded-md p-4" style={{ borderColor: 'var(--border-light)' }}>
          <h4 className="text-sm font-semibold mb-3 flex items-center" style={{ color: 'var(--text-primary)' }}>
            <svg className="w-4 h-4 mr-2" style={{ color: 'var(--color-success)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Support Information
          </h4>
          
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
              Support Contact
            </label>
            <input
              type="text"
              name="asset_support"
              value={formData.asset_support}
              onChange={handleInputChange}
              className="w-full border rounded-md px-3 py-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2"
              style={{
                borderColor: 'var(--border-light)',
                backgroundColor: 'var(--bg-card)',
                color: 'var(--text-primary)',
                '--tw-ring-color': 'var(--color-primary)'
              }}
              placeholder="support@company.com"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-3 border-t" style={{ borderColor: 'var(--border-light)' }}>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm"
            style={{ background: 'var(--gradient-primary)', color: 'white' }}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                Creating...
              </span>
            ) : (
              'Create System'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default AddSystemModal;
